import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useCourseOverridesMutations } from 'components/hooks/courseOverrideMutations';
import { useInfiniteCourseOverridesQuery } from 'components/hooks/courseOverridesQueries';

import Portal from 'components/application/Portal';
import SuccessToast from 'components/application/SuccessToast';

import { useBreadBoard } from 'components/contexts/Toaster';
import RemoveOverridesConfirmationModal from 'components/courses/RemoveOverridesConfirmationModal';

import CourseSidePanelOverridesTabList from './CourseSidePanelOverridesTabList';

export default function CourseSidePanelOverridesTab({
  courseId,
  courseName,
  isOverridesRemoveModalOpen,
  isSidePanelOpen,
  onOverrideRemove,
  onOverrideRemoveModalClose,
  onOverrideRemoveSuccess,
  selectedRequirementPriorityIds,
}) {
  const [overridesActiveScope, setOverridesActiveScope] = useState('all');
  const breadBoard = useBreadBoard();
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.CoursesTab',
  });

  const {
    data: courseOverrides,
    fetchNextPage: fetchNextCourseOverridesPage,
    hasNextPage: hasNextPageOfCourseOverrides,
    isFetching: isFetchingCourseOverrides,
    isFetchingNextPage: isFetchingNextPageOfCourseOverrides,
    isLoading: isLoadingCourseOverrides,
  } = useInfiniteCourseOverridesQuery({
    courseId,
    overridesActiveScope,
    queryOptions: {
      enabled: isSidePanelOpen && Boolean(courseId),
    },
  });

  const { createOverridesBulkRemovalMutation } = useCourseOverridesMutations({
    courseId,
  });

  const handleConfirmRemoveOverrides = () => {
    createOverridesBulkRemovalMutation.mutate(
      {
        overridesBulkRemovalParams: {
          personnel_course_connection_requirement_priority_ids:
            selectedRequirementPriorityIds,
        },
      },
      {
        onSuccess: (response) => {
          onOverrideRemoveSuccess();

          breadBoard.addToast(
            <SuccessToast
              message={i18nT('removeOverrides.successToast', {
                interpolation: { escapeValue: false },
                count: response.data.relationships.overrides.data.length,
                courseName,
              })}
            />,
          );
        },
        onError: () => {
          breadBoard.addInedibleToast();
        },
      },
    );
  };

  return (
    <>
      <CourseSidePanelOverridesTabList
        courseOverrides={courseOverrides}
        hasNextPageOfCourseOverrides={hasNextPageOfCourseOverrides}
        isFetchingCourseOverrides={isFetchingCourseOverrides}
        isFetchingNextPageOfCourseOverrides={
          isFetchingNextPageOfCourseOverrides
        }
        isLoadingCourseOverrides={isLoadingCourseOverrides}
        onNextCourseOverridesPageRequest={fetchNextCourseOverridesPage}
        onOverrideRemove={onOverrideRemove}
        onOverridesActiveScopeChange={setOverridesActiveScope}
        overridesActiveScope={overridesActiveScope}
        selectedRequirementPriorityIds={selectedRequirementPriorityIds}
      />
      {isOverridesRemoveModalOpen && (
        <Portal containerSelector='body'>
          <RemoveOverridesConfirmationModal
            onClose={onOverrideRemoveModalClose}
            onConfirmRemoveOverrides={handleConfirmRemoveOverrides}
            selectedRequirementPriorityIdsLength={
              selectedRequirementPriorityIds.length
            }
          />
        </Portal>
      )}
    </>
  );
}

CourseSidePanelOverridesTab.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  courseName: PropTypes.string.isRequired,
  isOverridesRemoveModalOpen: PropTypes.bool.isRequired,
  onOverrideRemoveModalClose: PropTypes.func.isRequired,
  onOverrideRemoveSuccess: PropTypes.func.isRequired,
  onOverrideRemove: PropTypes.func.isRequired,
  selectedRequirementPriorityIds: PropTypes.array.isRequired,
  isSidePanelOpen: PropTypes.bool.isRequired,
};
